// CareerPage.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from '../components/Breadcrumb';

const CareerPage = () => {
  const [activeDepartment, setActiveDepartment] = useState("all");
  const [activeLocation, setActiveLocation] = useState("all");
  
  // Toggle this to true to test "No Vacancies" state
  const [hasVacancies, setHasVacancies] = useState(true); // Change to false for no vacancies

  const departments = [
    { id: "all", name: "All Departments" },
    { id: "advisory", name: "Advisory" },
    { id: "investment", name: "Investment" },
    { id: "operations", name: "Operations" },
    { id: "technology", name: "Technology" },
    { id: "business-development", name: "Business Development" },
  ];

  const locations = [
    { id: "all", name: "All Locations" },
    { id: "new-york", name: "New York" },
    { id: "london", name: "London" },
    { id: "singapore", name: "Singapore" },
    { id: "dubai", name: "Dubai" },
  ];

  const openPositions = [
    {
      id: 1,
      slug: "senior-investment-associate",
      title: "Senior Investment Associate",
      department: "investment",
      location: "new-york",
      type: "Full-time",
      experience: "5-8 years",
      description:
        "We're seeking a Senior Investment Associate to join our growing team. You will play a key role in deal sourcing, due diligence, and portfolio management.",
      responsibilities: [
        "Lead due diligence processes for potential investments",
        "Develop financial models and investment memos",
        "Monitor portfolio company performance",
        "Support senior partners in deal execution",
      ],
      requirements: [
        "5-8 years of experience in investment banking or private equity",
        "Strong financial modeling and analytical skills",
        "Excellent communication and presentation abilities",
        "MBA or relevant advanced degree preferred",
      ],
    },
    {
      id: 2,
      slug: "financial-analyst",
      title: "Financial Analyst",
      department: "advisory",
      location: "new-york",
      type: "Full-time",
      experience: "2-4 years",
      description:
        "Join our advisory practice as a Financial Analyst, supporting clients with financial analysis, modeling, and strategic recommendations.",
      responsibilities: [
        "Build and maintain complex financial models",
        "Conduct market research and competitive analysis",
        "Prepare client presentations and reports",
        "Support senior advisors on client engagements",
      ],
      requirements: [
        "2-4 years of experience in finance or consulting",
        "Proficiency in Excel and financial modeling",
        "Strong analytical and problem-solving skills",
        "Bachelor's degree in Finance, Economics, or related field",
      ],
    },
    {
      id: 3,
      slug: "operations-manager",
      title: "Operations Manager",
      department: "operations",
      location: "london",
      type: "Full-time",
      experience: "6-10 years",
      description:
        "We're looking for an experienced Operations Manager to optimize our internal processes and support portfolio company operations.",
      responsibilities: [
        "Streamline internal workflows and systems",
        "Lead operational improvement initiatives",
        "Manage vendor relationships and contracts",
        "Coordinate cross-functional projects",
      ],
      requirements: [
        "6-10 years of operations management experience",
        "Strong project management and leadership skills",
        "Experience in professional services or finance preferred",
        "PMP certification is a plus",
      ],
    },

  ];

  const filteredPositions = hasVacancies ? openPositions.filter((position) => {
    const matchesDepartment =
      activeDepartment === "all" || position.department === activeDepartment;
    const matchesLocation =
      activeLocation === "all" || position.location === activeLocation;
    return matchesDepartment && matchesLocation;
  }) : [];

  return (
    <>
      <Breadcrumb />
      <div className="career-page">
        {/* Hero Section */}
        <section className="career-hero">
          <div className="museum-wrap">
            <div className="career-hero-content">
              <span className="section-label">Join Our Team</span>
              <h1>
                Build Your <em>Career</em> at Morphis
              </h1>
              <p className="career-hero-subtitle">
                We're looking for talented individuals who share our passion for
                excellence and innovation. Join a team that's shaping the future
                of capital markets advisory.
              </p>
            </div>
          </div>
        </section>

        {/* Open Positions Section */}
        <section id="open-positions" className="career-positions">
          <div className="museum-wrap">
            <div className="positions-header">
              <span className="section-label">Career Opportunities</span>
              <h2>Current Openings</h2>
            </div>

            {/* Only show filters when there are vacancies */}
            {hasVacancies && openPositions.length > 0 && (
              <div className="positions-filters">
                <div className="filter-group">
                  <label>Department</label>
                  <select
                    value={activeDepartment}
                    onChange={(e) => setActiveDepartment(e.target.value)}
                  >
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Location</label>
                  <select
                    value={activeLocation}
                    onChange={(e) => setActiveLocation(e.target.value)}
                  >
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Case 1: No Vacancies at All */}
            {!hasVacancies && (
              <div className="no-vacancies-state">
                <div className="no-vacancies-icon">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 21V5C16 3.89543 15.1046 3 14 3H10C8.89543 3 8 3.89543 8 5V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 11V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M10 13H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>No Open Positions at This Time</h3>
                <p>
                  While we don't have any current vacancies, we're always interested in 
                  connecting with talented professionals who share our vision.
                </p>
                <button 
                  className="future-interest-btn"
                  onClick={() => {
                    window.location.href = "mailto:careers@morphis.com?subject=Future%20Opportunity%20Interest";
                  }}
                >
                  Express Future Interest
                </button>
                <p className="keep-in-touch">
                  You can also follow us on LinkedIn for updates on future opportunities.
                </p>
              </div>
            )}

            {/* Case 2: Vacancies exist but none match filters */}
            {hasVacancies && filteredPositions.length === 0 && openPositions.length > 0 && (
              <div className="no-positions">
                <div className="no-positions-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 10.5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6H2V10.5H22V6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 12V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M8 14H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <p>No open positions match your current filters.</p>
                <button
                  onClick={() => {
                    setActiveDepartment("all");
                    setActiveLocation("all");
                  }}
                  className="reset-filters-btn"
                >
                  Clear Filters
                </button>
              </div>
            )}

            {/* Case 3: Vacancies exist and match filters */}
            {hasVacancies && filteredPositions.length > 0 && (
              <div className="positions-list">
                {filteredPositions.map((position) => (
                  <div key={position.id} className="position-card">
                    <div className="position-header">
                      <h3>{position.title}</h3>
                      <span className="position-type">{position.type}</span>
                    </div>
                    <div className="position-meta">
                      <span className="position-department">
                        {
                          departments.find((d) => d.id === position.department)
                            ?.name
                        }
                      </span>
                      <span className="position-location">
                        {locations.find((l) => l.id === position.location)?.name}
                      </span>
                      <span className="position-experience">
                        {position.experience}
                      </span>
                    </div>
                    <p className="position-description">{position.description}</p>
                    <Link
                      to={`/careers/${position.slug}`}
                      className="view-details-link"
                    >
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>



        <style>{`
          .career-page {
            background: var(--bg-main, #fffdf6);
            min-height: 100vh;
          }

          /* Hero Section */
          .career-hero {
            padding-top: 88px;
            background: var(--bg-main, #fffdf6);
            border-bottom: 1px solid var(--border-light, #e6e0d4);
          }

          .career-hero-content {
            padding: 60px 0 80px;
            max-width: 800px;
          }

          .section-label {
            display: inline-block;
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: var(--color-orange, #e87722);
            margin-bottom: 20px;
          }

          .career-hero-content h1 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: clamp(2.5rem, 5vw, 4rem);
            line-height: 1.1;
            margin-bottom: 1.5rem;
            color: var(--color-dark, #1a1a1a);
          }

          .career-hero-content h1 em {
            font-style: italic;
            color: var(--color-orange, #e87722);
          }

          .career-hero-subtitle {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: var(--fs-sub-line, 1.15rem);
            color: var(--text-muted, #6b7280);
            line-height: 1.6;
            margin-bottom: 32px;
          }

          /* Open Positions Section */
          .career-positions {
            padding: 80px 0;
            background: var(--bg-main, #fffdf6);
          }

          .positions-header {
            text-align: center;
            margin-bottom: 50px;
          }

          .positions-header h2 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: clamp(1.75rem, 4vw, 2.5rem);
            color: var(--color-dark, #1a1a1a);
          }

          .positions-filters {
            display: flex;
            gap: 24px;
            justify-content: center;
            margin-bottom: 50px;
            flex-wrap: wrap;
          }

          .filter-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .filter-group label {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--text-muted, #6b7280);
          }

          .filter-group select {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 14px;
            padding: 10px 16px;
            border: 1px solid var(--border-light, #e6e0d4);
            background: transparent;
            cursor: pointer;
            min-width: 180px;
          }

          .positions-list {
            display: flex;
            flex-direction: column;
            gap: 24px;
          }

          .position-card {
            border: 1px solid var(--border-light, #e6e0d4);
            padding: 28px;
            transition: all 0.3s ease;
          }

          .position-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
          }

          .position-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
            margin-bottom: 16px;
          }

          .position-header h3 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: 1.3rem;
            color: var(--color-dark, #1a1a1a);
          }

          .position-type {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.08em;
            padding: 4px 12px;
            background: var(--color-orange, #e87722);
            color: #fff;
            text-transform: uppercase;
          }

          .position-meta {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
            margin-bottom: 16px;
          }

          .position-meta span {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 12px;
            color: var(--text-muted, #6b7280);
          }

          .position-description {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 0.9rem;
            line-height: 1.6;
            color: var(--text-sub, #2d2d2d);
            margin-bottom: 20px;
          }

          .view-details-link {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color-dark, #1a1a1a);
            text-decoration: none;
            transition: all 0.2s;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }

          .view-details-link:hover {
            color: var(--color-orange, #e87722);
            transform: translateX(4px);
          }

          /* No Vacancies State */
          .no-vacancies-state {
            text-align: center;
            padding: 60px 40px;
            max-width: 600px;
            margin: 0 auto;
            background: #faf8f4;
            border: 1px solid var(--border-light, #e6e0d4);
          }

          .no-vacancies-icon {
            margin-bottom: 24px;
            color: var(--text-muted, #6b7280);
          }

          .no-vacancies-state h3 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: 1.5rem;
            color: var(--color-dark, #1a1a1a);
            margin-bottom: 16px;
          }

          .no-vacancies-state p {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 0.95rem;
            line-height: 1.6;
            color: var(--text-muted, #6b7280);
            margin-bottom: 28px;
          }

          .future-interest-btn {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            padding: 14px 32px;
            background: var(--color-orange, #e87722);
            color: #fff;
            border: none;
            cursor: pointer;
            transition: all 0.2s;
            margin-bottom: 20px;
          }

          .future-interest-btn:hover {
            background: var(--color-dark, #1a1a1a);
            transform: translateY(-2px);
          }

          .keep-in-touch {
            font-size: 0.85rem !important;
            margin-bottom: 0 !important;
          }

          /* No Positions (filtered) State */
          .no-positions {
            text-align: center;
            padding: 60px 40px;
          }

          .no-positions-icon {
            margin-bottom: 20px;
            color: var(--text-muted, #6b7280);
          }

          .no-positions p {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 1rem;
            color: var(--text-muted, #6b7280);
            margin-bottom: 24px;
          }

          .reset-filters-btn {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            padding: 10px 24px;
            background: transparent;
            color: var(--color-orange, #e87722);
            border: 1px solid var(--color-orange, #e87722);
            cursor: pointer;
            transition: all 0.2s;
          }

          .reset-filters-btn:hover {
            background: var(--color-orange, #e87722);
            color: #fff;
          }

          /* Apply CTA Section */
          .career-apply {
            padding: 80px 0;
            background: #faf8f4;
            border-top: 1px solid var(--border-light, #e6e0d4);
          }

          .apply-content {
            text-align: center;
            max-width: 600px;
            margin: 0 auto;
          }

          .apply-content h2 {
            font-family: var(--font-heading, "IBM Plex Serif", serif);
            font-size: clamp(1.5rem, 4vw, 2rem);
            margin-bottom: 16px;
            color: var(--color-dark, #1a1a1a);
          }

          .apply-content p {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 0.95rem;
            color: var(--text-muted, #6b7280);
            margin-bottom: 32px;
            line-height: 1.6;
          }

          .apply-btn {
            font-family: var(--font-body, Arial, sans-serif);
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            padding: 14px 32px;
            background: var(--color-orange, #e87722);
            color: #fff;
            border: none;
            cursor: pointer;
            transition: all 0.2s;
          }

          .apply-btn:hover {
            background: var(--color-dark, #1a1a1a);
            transform: translateY(-2px);
          }

          /* Responsive */
          @media (max-width: 768px) {
            .career-hero {
              padding-top: 68px;
            }

            .career-hero-content {
              padding: 40px 0 60px;
            }

            .positions-filters {
              flex-direction: column;
            }

            .filter-group select {
              width: 100%;
            }

            .position-header {
              flex-direction: column;
              align-items: flex-start;
            }

            .no-vacancies-state {
              padding: 40px 24px;
            }

            .no-vacancies-state h3 {
              font-size: 1.25rem;
            }
          }

          @media (max-width: 480px) {
            .position-card {
              padding: 20px;
            }

            .position-header h3 {
              font-size: 1.1rem;
            }

            .future-interest-btn {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default CareerPage;